import { useEffect, useState } from "react";

import {
  Loader,
  Modal,
  ModalBody,
  ModalHeader,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button, Textarea } from "@transfr-inc/dashboard-components/forms";

import { useNotification } from "@hooks/use-notification";
import { services } from "@src/dependencies";
import { useApiRequest } from "@utils/http-client";

/**
 * Dev tooling: view and edit the Gemini prompts behind deck generation.
 * Each dial resolves to its active stored version, else the code default;
 * saving stores a new version that the next generation picks up.
 */
export function ImpactReportPromptConfigModal({ open, onClose, reportType }) {
  const { impactReportService } = services;
  const { addNotification } = useNotification();

  // field -> edited body; only fields the user touched are tracked
  const [edits, setEdits] = useState({});
  const [savingField, setSavingField] = useState();

  const {
    loading,
    response,
    sendRequest: refetch,
  } = useApiRequest(
    () => impactReportService.getPromptConfig(reportType),
    false,
  );

  useEffect(() => {
    if (open) {
      setEdits({});
      refetch();
    }
  }, [open, reportType]);

  const onSave = async (field) => {
    setSavingField(field);
    try {
      await impactReportService.savePrompt(reportType, field, edits[field]);
      setEdits((current) => {
        const rest = { ...current };
        delete rest[field];
        return rest;
      });
      addNotification({
        type: NotificationType.success,
        message: `Prompt "${field}" saved.`,
        ttl: 5000,
      });
      await refetch();
    } catch (error) {
      console.error("[Impact Report] - Failed to save prompt", error);
      addNotification({
        type: NotificationType.error,
        message:
          error?.data?.detail ?? "We couldn't save the prompt. Please retry.",
        ttl: 8000,
      });
    } finally {
      setSavingField();
    }
  };

  return (
    <Modal
      modalClassName="impact-report-prompt-config-modal"
      open={open}
      onClose={onClose}
      uniqueName="impact-report-prompt-config-modal"
    >
      <ModalHeader
        className="blue-icon small"
        icon={["fa-regular", "fa-sliders"]}
        title="Prompt Config"
        uniqueName="Prompt Config"
      />
      <ModalBody>
        <p className="impact-report-prompt-config-hint">
          Dev tooling — edits are stored as new versions and used by the next
          generation for this report type. Fields without a stored version use
          the code default.
        </p>
        {loading && <Loader overlay />}
        {response?.prompts?.map((dial) => {
          const edited = edits[dial.field] !== undefined;
          return (
            <div key={dial.field} className="impact-report-prompt-dial">
              <div className="impact-report-prompt-dial-header">
                <span className="impact-report-prompt-dial-name">
                  {dial.field}
                </span>
                <span
                  className={`impact-report-prompt-dial-badge ${
                    dial.isDefault ? "" : "stored"
                  }`}
                >
                  {dial.isDefault ? "default" : `stored v${dial.version}`}
                </span>
                <Button
                  size="small"
                  disabled={
                    !edited || !edits[dial.field]?.trim() || !!savingField
                  }
                  loader={savingField === dial.field}
                  onClick={() => onSave(dial.field)}
                >
                  Save
                </Button>
              </div>
              <Textarea
                className="impact-report-prompt-dial-body"
                value={edited ? edits[dial.field] : dial.body}
                onChange={({ target }) =>
                  setEdits((current) => ({
                    ...current,
                    [dial.field]: target.value,
                  }))
                }
              />
            </div>
          );
        })}
      </ModalBody>
    </Modal>
  );
}
